/**
 * Financial Module React Query Hooks
 *
 * Sprint 6A - Business Analytics
 * Provides data fetching hooks with caching and automatic refetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as financialApi from '@/lib/api/financial';
import type {
  CreateEntityFinancialRecordInput,
  CreateFinancialPeriodInput,
  CreateNormalisationInput,
  CreateCustomLineItemInput,
  UpdateCustomLineItemInput,
  CustomLineItemStatementType,
  FinancialCascadeView,
} from '@/types/financial';

// ============================================================================
// Query Key Factory (F004 — #586)
// ============================================================================

/**
 * Centralised query key factory for financial data.
 * Exported so that hooks and callers can invalidate caches consistently.
 */
export const FINANCIAL_QUERY_KEYS = {
  profitLoss: (periodId: string) => ['financial', 'profit-loss', periodId] as const,
  cascade: (entityId: string, periodId: string | undefined, view: FinancialCascadeView) =>
    ['financial', 'cascade', entityId, periodId, view] as const,
  balanceSheet: (periodId: string) => ['financial', 'balance-sheet', periodId] as const,
  periods: (entityRecordId: string) => ['financial', 'periods', entityRecordId] as const,
  cashFlow: (periodId: string) => ['financial', 'cashflow', periodId] as const,
  normalisations: (periodId: string) => ['financial', 'normalisations', periodId] as const,
  ebitdaSummary: (periodId: string) => ['financial', 'ebitda-summary', periodId] as const,
  ratios: (periodId: string) => ['financial', 'ratios', periodId] as const,
  healthScore: (periodId: string) => ['financial', 'health-score', periodId] as const,
};

// ============================================================================
// Entity Financial Record
// ============================================================================

export function useEntityRecord(accountId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'entity-record', accountId],
    queryFn: () => (accountId ? financialApi.getEntityRecord(accountId) : null),
    enabled: !!accountId,
  });
}

export function useCreateEntityRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEntityFinancialRecordInput) =>
      financialApi.createEntityRecord(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'entity-record', data.accountId] });
    },
  });
}

export function useUpdateEntityRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Partial<CreateEntityFinancialRecordInput>) =>
      financialApi.updateEntityRecord(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'entity-record', data.accountId] });
    },
  });
}

// ============================================================================
// Financial Periods
// ============================================================================

export function useFinancialPeriods(entityRecordId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'periods', entityRecordId],
    queryFn: () => (entityRecordId ? financialApi.getPeriods(entityRecordId) : []),
    enabled: !!entityRecordId,
  });
}

export function useCreatePeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFinancialPeriodInput) => financialApi.createPeriod(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['financial', 'periods', data.entityFinancialRecordId],
      });
    },
  });
}

export function useLockPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) => financialApi.lockPeriod(periodId),
    onSuccess: ({ period }) => Promise.all([
      queryClient.invalidateQueries({ queryKey: ['financial', 'periods', period.entityFinancialRecordId] }),
      queryClient.invalidateQueries({ queryKey: ['financial', 'ratios', period.id] }),
    ]),
  });
}

// ============================================================================
// Profit & Loss
// ============================================================================

export function useProfitLoss(periodId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'profit-loss', periodId],
    queryFn: () => (periodId ? financialApi.getProfitLoss(periodId) : []),
    enabled: !!periodId,
  });
}

export function useFinancialCascade(
  entityId: string | undefined,
  periodId: string | undefined,
  view: FinancialCascadeView,
) {
  return useQuery({
    queryKey: entityId
      ? FINANCIAL_QUERY_KEYS.cascade(entityId, periodId, view)
      : ['financial', 'cascade', entityId, periodId, view],
    queryFn: () => financialApi.getFinancialCascade(entityId!, { periodId, view }),
    enabled: !!entityId,
  });
}

export function useRecalculatePL() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) => financialApi.recalculatePL(periodId),
    onSuccess: (_, periodId) => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'profit-loss', periodId] });
    },
  });
}

// ============================================================================
// Balance Sheet
// ============================================================================

export function useBalanceSheet(periodId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'balance-sheet', periodId],
    queryFn: () => (periodId ? financialApi.getBalanceSheet(periodId) : []),
    enabled: !!periodId,
  });
}

export function useValidateBalanceSheet() {
  return useMutation({
    mutationFn: (periodId: string) => financialApi.validateBalanceSheet(periodId),
  });
}

// ============================================================================
// Cash Flow
// ============================================================================

export function useCashFlow(periodId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'cashflow', periodId],
    queryFn: () => (periodId ? financialApi.getCashFlow(periodId) : []),
    enabled: !!periodId,
  });
}

export function useRegenerateCashFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) => financialApi.regenerateCashFlow(periodId),
    onSuccess: (_, periodId) => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'cashflow', periodId] });
    },
  });
}

// ============================================================================
// EBITDA Normalization
// ============================================================================

export function useNormalisations(periodId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'normalisations', periodId],
    queryFn: () => (periodId ? financialApi.getNormalisations(periodId) : []),
    enabled: !!periodId,
  });
}

export function useCreateNormalisation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNormalisationInput) => financialApi.createNormalisation(input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['financial', 'normalisations', data.financialPeriodId],
      });
      queryClient.invalidateQueries({
        queryKey: ['financial', 'ebitda-summary', data.financialPeriodId],
      });
    },
  });
}

export function useDeleteNormalisation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, periodId: _periodId }: { id: string; periodId: string }) =>
      financialApi.deleteNormalisation(id),
    onSuccess: (_, { periodId }) => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'normalisations', periodId] });
      queryClient.invalidateQueries({ queryKey: ['financial', 'ebitda-summary', periodId] });
    },
  });
}

export function useNormalisationTemplates() {
  return useQuery({
    queryKey: ['financial', 'normalisation-templates'],
    queryFn: () => financialApi.getNormalisationTemplates(),
    staleTime: 1000 * 60 * 60, // 1 hour (templates rarely change)
  });
}

export function useEbitdaSummary(periodId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'ebitda-summary', periodId],
    queryFn: () => (periodId ? financialApi.calculateEbitdaSummary(periodId) : null),
    enabled: !!periodId,
  });
}

// ============================================================================
// Financial Ratios
// ============================================================================

export function useRatios(periodId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'ratios', periodId],
    queryFn: () => (periodId ? financialApi.getRatios(periodId) : []),
    enabled: !!periodId,
  });
}

export function useCalculateRatios() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) => financialApi.calculateRatios(periodId),
    onSuccess: (_, periodId) => {
      return queryClient.invalidateQueries({ queryKey: ['financial', 'ratios', periodId], refetchType: 'all' });
    },
  });
}

// ============================================================================
// Health Score
// ============================================================================

export function useHealthScore(periodId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'health-score', periodId],
    queryFn: () => (periodId ? financialApi.getHealthScore(periodId) : null),
    enabled: !!periodId,
  });
}

/**
 * Fetch health scores by entity record ID.
 * Returns { latestScore, history } for Health Score Panel.
 */
export function useHealthScores(recordId: string | undefined) {
  return useQuery({
    queryKey: ['financial', 'health-scores', recordId],
    queryFn: () => (recordId ? financialApi.getHealthScores(recordId) : { latestScore: null, history: [] }),
    enabled: !!recordId,
  });
}

export function useCalculateHealthScore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) => financialApi.calculateHealthScore(periodId),
    onSuccess: (_, periodId) => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'health-score', periodId] });
    },
  });
}

/**
 * Calculate health score by entity record ID.
 * Used by Health Score Panel recalculate button.
 */
export function useCalculateHealthScoreByRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityFinancialRecordId, basedOnPeriodId }: { entityFinancialRecordId: string; basedOnPeriodId?: string }) =>
      financialApi.calculateHealthScoreByRecord(entityFinancialRecordId, basedOnPeriodId),
    onSuccess: (_, { entityFinancialRecordId }) => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'health-scores', entityFinancialRecordId] });
    },
  });
}

// ============================================================================
// Custom Line Items (Sprint 11)
// ============================================================================

export function useCustomLineItems(
  entityRecordId: string | undefined,
  statementType: CustomLineItemStatementType | undefined
) {
  return useQuery({
    queryKey: ['financial', 'custom-line-items', entityRecordId, statementType],
    queryFn: () =>
      entityRecordId && statementType
        ? financialApi.getCustomLineItems(entityRecordId, statementType)
        : { definitions: [], values: [] },
    enabled: !!entityRecordId && !!statementType,
  });
}

export function useCreateCustomLineItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomLineItemInput) =>
      financialApi.createCustomLineItem(input),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({
        queryKey: ['financial', 'custom-line-items', input.entityFinancialRecordId, input.statementType],
      });
    },
  });
}

export function useUpdateCustomLineItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCustomLineItemInput }) =>
      financialApi.updateCustomLineItem(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['financial', 'custom-line-items', data.entityFinancialRecordId, data.statementType],
      });
    },
  });
}

export function useDeleteCustomLineItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      entityRecordId: _entityRecordId,
      statementType: _statementType,
    }: {
      id: string;
      entityRecordId: string;
      statementType: CustomLineItemStatementType;
    }) => financialApi.deleteCustomLineItem(id),
    onSuccess: (_, { entityRecordId, statementType }) => {
      queryClient.invalidateQueries({
        queryKey: ['financial', 'custom-line-items', entityRecordId, statementType],
      });
      // Also invalidate profit-loss and balance-sheet queries as custom items affect those views
      queryClient.invalidateQueries({
        queryKey: ['financial', 'profit-loss'],
      });
      queryClient.invalidateQueries({
        queryKey: ['financial', 'balance-sheet'],
      });
    },
  });
}

export function useUpsertCustomLineItemValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      customLineItemId: string;
      financialPeriodId: string;
      amount: number;
      notes?: string;
      entityRecordId: string;
      statementType: CustomLineItemStatementType;
    }) =>
      financialApi.upsertCustomLineItemValue({
        customLineItemId: input.customLineItemId,
        financialPeriodId: input.financialPeriodId,
        amount: input.amount,
        notes: input.notes,
      }),
    onSuccess: (_, { entityRecordId, statementType }) => {
      queryClient.invalidateQueries({
        queryKey: ['financial', 'custom-line-items', entityRecordId, statementType],
      });
    },
  });
}

// ============================================================================
// Bulk Import (CSV/Excel — F004 fix for #586)
// ============================================================================

export interface BulkImportPayload {
  profitLoss: Array<{ lineItemType: string; amount: number }>;
  balanceSheet: Array<{ lineItemType: string; amount: number }>;
}

export interface BulkImportResult {
  created: { profitLoss: number; balanceSheet: number };
}

/**
 * useBulkImport
 *
 * Wraps the bulk-import API endpoint and invalidates P&L and BS caches
 * after a successful import so that the statements reflect the new data
 * without requiring a page refresh (fixes #586 AC002).
 */
export function useBulkImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      periodId,
      payload,
    }: {
      periodId: string;
      payload: BulkImportPayload;
    }): Promise<BulkImportResult> => {
      const { apiRequest } = await import('@/lib/queryClient');
      const response = await apiRequest(
        'POST',
        `/api/financial/periods/${periodId}/bulk-import`,
        payload
      );
      return response.json();
    },
    onSuccess: (_data, { periodId }) => {
      // Invalidate P&L and BS caches for the imported period so tables
      // reflect the new data without a page refresh.
      queryClient.invalidateQueries({
        queryKey: ['financial', 'profit-loss', periodId],
      });
      queryClient.invalidateQueries({
        queryKey: ['financial', 'balance-sheet', periodId],
      });
    },
  });
}

// ============================================================================
// Multi-Year Bulk Import (#588 — EDG-834)
// ============================================================================

export interface MultiYearBulkImportPayload {
  periods: Record<string, string>;
  items: Record<string, BulkImportPayload>;
}

export interface MultiYearBulkImportResult {
  results: Record<string, { created: { profitLoss: number; balanceSheet: number } }>;
}

/**
 * useBulkImportMultiYear
 *
 * Imports financial data across multiple FY periods in a single atomic operation.
 * Invalidates caches for all affected periods on success.
 */
export function useBulkImportMultiYear() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: MultiYearBulkImportPayload): Promise<MultiYearBulkImportResult> => {
      const { apiRequest } = await import('@/lib/queryClient');
      const response = await apiRequest(
        'POST',
        '/api/financial/bulk-import-multi-year',
        payload
      );
      return response.json();
    },
    onSuccess: (_data, payload) => {
      // Invalidate caches for all imported periods
      for (const periodId of Object.values(payload.periods)) {
        queryClient.invalidateQueries({
          queryKey: FINANCIAL_QUERY_KEYS.profitLoss(periodId),
        });
        queryClient.invalidateQueries({
          queryKey: FINANCIAL_QUERY_KEYS.balanceSheet(periodId),
        });
      }
    },
  });
}
